import {
	computeContextMenuTitle,
	ContextMenu,
	DateString,
	TableView,
	VirtualTableViewColumnDefinition,
	useContextMenu,
	TableViewNoResultConfig
} from "client/Components";
import {
	MultiSelectableOptions,
	SelectableItemOptions,
	useRowHover,
	useUserTimezone
} from "client/Hooks";
import { getDisplayFriendlySize } from "client_legacy/Documents/fileExtensions";
import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useImperativeHandle,
	useMemo,
	useRef
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { DocumentName, DocumentNameKind } from "../../DocumentName";
import { GridItem } from "./SharePointRecentlyModified";
import { TimeZone } from "client/DateAndTime/timezones";
import { formatFileLocation } from "client/Features/Layout/TitleBar/GlobalSearch/Items/DocumentListItem";
import { RecentDocumentAction, RecentDocumentActionContext } from "./actions";
import { DocumentNameWithQuickActions } from "../../DocumentNameWithQuickActions";
import { RecentDocumentItemViewModel } from "client/Api/Documents/SharePoint/common";
import { generateTrackingId, isNotNil } from "client/Misc/utils";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export type SharePointTableViewHandle = {
	resetScrollPosition: () => void;
};

export type SharePointRecentlyModifiedTableViewProps = {
	items: readonly GridItem[];
	multiSelection: MultiSelectableOptions;
	selectionActions: readonly RecentDocumentAction[];
	actionContext: RecentDocumentActionContext;
	isTouch: boolean;
	hasSearch: boolean;
	selectionFor: (item: GridItem) => SelectableItemOptions;
	onOpenItem: (item: RecentDocumentItemViewModel) => void;
};

export const SharePointRecentlyModifiedTableView = forwardRef(
	(props: SharePointRecentlyModifiedTableViewProps, ref: Ref<SharePointTableViewHandle>): ReactElement => {
		const {
			items,
			multiSelection,
			selectionActions,
			actionContext,
			isTouch,
			hasSearch,
			selectionFor,
			onOpenItem
		} = props;

		const [hoveredRowId, hoverHandler] = useRowHover<string>();
		const userTimezone: TimeZone = useUserTimezone();
		const tableRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
		const [contextMenuHandler, contextMenuProps] = useContextMenu(tableRef);

		useImperativeHandle(
			ref,
			() => ({
				resetScrollPosition: () => {
					tableRef.current
						?.scrollTo(0, 0);
				}
			}),
			[]
		);

		const noResultConfig = useMemo(
			(): TableViewNoResultConfig | undefined => {
				return hasSearch
					? {
						icon: "magnifying-glass",
						title: localize("_NoSearchResults"),
						message: localize("_PleaseTrySearchingWithAnotherTerm")
					}
					: {
						icon: "folder-open",
						title: localize("_NoDocumentsToShow"),
						message: localize("_ThereAreCurrentlyNoDocumentsInThisFolder")
					};
			},
			[hasSearch]
		);

		return (
			<>
				<TableView
					isVirtual
					hasNoBottomDelimiter
					columns={columns}
					items={items}
					height="fill"
					itemKey="uniqueKey"
					trackingId="sharepoint-recently-modified-grid"
					multiSelectable={multiSelection}
					ref={tableRef}
					noResultConfig={noResultConfig}
					renderAfterItems={
						items.length > 0
							? <AiAssistantFabSpacing />
							: undefined
					}
				>
					{(x): ReactElement => {
						const selectable: SelectableItemOptions = selectionFor(x);
						const isSelected: boolean = selectable && selectable.isSelected;

						return (
							<TableView.Row
								selectable={selectionFor(x)}
								onContextMenu={contextMenuHandler}
								trackingId={generateTrackingId("recent-document-item", `|${x.uniqueKey}`)}
								onIsHoverChange={(isHover: boolean) => hoverHandler(x.uniqueKey, isHover)}
							>
								<TableView.Cell>
									<DocumentNameWithQuickActions
										selectionActions={selectionActions}
										actionContext={{...actionContext, selection: [x.model]}}
										isTouch={isTouch}
										isSelected={isSelected}
										isRowHovered={hoveredRowId === x.uniqueKey}
										name={x.name}
									>
										<DocumentName
											name={x.name}
											documentKind={x.model.itemType}
											mode={{
												kind: DocumentNameKind.Default,
												hasBookmark: false
											}}
											onClick={() => onOpenItem(x.model)}
										/>
									</DocumentNameWithQuickActions>
								</TableView.Cell>

								<TableView.Cell>
									{isNotNil(x.modifiedOn) && (
										<DateString
											value={x.modifiedOn}
											format="RelativeTimestamp"
											timezone={userTimezone}
										/>
									)}
								</TableView.Cell>

								<TableView.Cell>
									{x.modifiedBy}
								</TableView.Cell>

								<TableView.Cell>
									{x.createdBy}
								</TableView.Cell>

								<TableView.Cell>
									{x.extension}
								</TableView.Cell>

								<TableView.Cell>
									{getDisplayFriendlySize(x.size)}
								</TableView.Cell>

								<TableView.Cell>
									{formatFileLocation(x.originalLocation)}
								</TableView.Cell>
							</TableView.Row>
						);
					}}
				</TableView>

				{contextMenuProps && (
					<ContextMenu
						{...contextMenuProps}
						actions={selectionActions}
						context={actionContext}
						trackingId="sharepoint-recent-document-context-menu"
						title={computeContextMenuTitle(
							actionContext.selection,
							(x) => x.name
						)}
					/>
				)}
			</>
		);
	}
);
SharePointRecentlyModifiedTableView.displayName = "SharePointRecentlyModifiedTableView";

const columns: readonly VirtualTableViewColumnDefinition[] = [
	{
		title: localize("_Name"),
		trackingId: "name",
		width: 400
	},
	{
		title: localize("_DateModified"),
		trackingId: "date-modified",
		width: 150
	},
	{
		title: localize("_ModifiedBy"),
		trackingId: "modified-by",
		width: 150
	},
	{
		title: localize("_CreatedBy"),
		trackingId: "created-by",
		width: 150
	},
	{
		title: localize("_Extension"),
		trackingId: "extension",
		width: 150
	},
	{
		title: localize("_FileSize"),
		trackingId: "size",
		width: 150
	},
	{
		title: localize("_OriginalLocation"),
		trackingId: "original-location",
		width: 400
	}
];